import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { errorApiRef, ErrorApi, useApi } from '@backstage/frontend-plugin-api';
import {
  AccountEntity,
  AuthType,
  RulesCompliance,
  ScanStatusResponse,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  AsyncTaskInfo,
  AsyncTaskType,
} from '@internal/backstage-plugin-devops-async-tasks-common';
import {
  asyncTasksApiRef,
  AsyncTasksApi,
} from '@internal/backstage-plugin-devops-async-tasks-react';
import {
  secretsStorageApiRef,
  SecretsStorageApi,
} from '@internal/backstage-plugin-devops-secrets-storage-react';
import { AwsCredential, AzureLoadFiles, PendingScan } from './types';
import {
  AccountCloudResourcesStatistics,
  CloudResourcesStatisticsApi,
  cloudResourcesStatisticsApiRef,
  rulesStatisticsApiRef,
} from '../../api';

interface AccountContextProviderProps {
  children: ReactNode;
  accountName: string;
}

enum ActionTypes {
  SCAN_STARTED = 'SCAN_STARTED',
  LOAD_COMPLETED = 'LOAD_COMPLETED',
  INITIAL_LOAD_COMPLETED = 'INITIAL_LOAD_COMPLETED',
  START_SCAN = 'START_SCAN',
  CHANGE_CREDENTIAL_DIALOG_STATUS = 'CHANGE_DIALOG',
  CHANGE_AUTH_TYPE = 'CHANGE_AUTH_TYPE',
  CHANGE_ACCOUNT_ENTITY = 'CHANGE_ACCOUNT_ENTITY',
  COMPLIANCE_LOADED = 'COMPLIANCE_LOADED',
  OPEN_SETTINGS = 'OPEN_SETTINGS',
  CLOSE_SETTINGS = 'CLOSE_SETTINGS',
  OPEN_CONFIRM_DIALOG = 'OPEN_CONFIRM_DIALOG',
  CLOSE_CONFIRM_DIALOG = 'CLOSE_CONFIRM_DIALOG',
}

type ScanStartedAction = {
  type: ActionTypes.SCAN_STARTED;
  payload: {
    task: AsyncTaskInfo | undefined;
    scanStatus: ScanStatusResponse | undefined;
  };
};

type LoadCompletedAction = {
  type: ActionTypes.LOAD_COMPLETED;
  payload: {
    task: AsyncTaskInfo | undefined;
    scanStatus: ScanStatusResponse | undefined;
    // True when the backend confirmed there is no active task anymore.
    // Absent when the lookup itself failed — the old state must survive.
    taskFinished?: boolean;
  };
};

type InitialLoadCompletedAction = {
  type: ActionTypes.INITIAL_LOAD_COMPLETED;
  payload: {
    task: AsyncTaskInfo | undefined;
    scanStatus: ScanStatusResponse | undefined;
    cloudResourcesStatistics: AccountCloudResourcesStatistics | undefined;
    taskFinished?: boolean;
  };
};

type StartScanAction = {
  type: ActionTypes.START_SCAN;
  payload: {
    credential: AwsCredential | undefined;
    azureFiles: AzureLoadFiles | undefined;
  };
};

type OpenConfirmDialogAction = {
  type: ActionTypes.OPEN_CONFIRM_DIALOG;
  payload: PendingScan | undefined;
};

type CloseConfirmDialogAction = {
  type: ActionTypes.CLOSE_CONFIRM_DIALOG;
};

type ChangeCredentialDialogStatus = {
  type: ActionTypes.CHANGE_CREDENTIAL_DIALOG_STATUS;
  payload: boolean;
};

type ChangeAuthTypeAction = {
  type: ActionTypes.CHANGE_AUTH_TYPE;
  payload: AuthType | undefined;
};

type ChangeAccountEntityAction = {
  type: ActionTypes.CHANGE_ACCOUNT_ENTITY;
  payload: AccountEntity | undefined;
};

type ComplianceLoadedAction = {
  type: ActionTypes.COMPLIANCE_LOADED;
  payload: Record<string, RulesCompliance>;
};

type ChangeSettingsStatusAction = {
  type: ActionTypes.OPEN_SETTINGS | ActionTypes.CLOSE_SETTINGS;
};

type AccountAction =
  | ScanStartedAction
  | LoadCompletedAction
  | InitialLoadCompletedAction
  | StartScanAction
  | ChangeCredentialDialogStatus
  | ChangeAuthTypeAction
  | ChangeAccountEntityAction
  | ComplianceLoadedAction
  | ChangeSettingsStatusAction
  | OpenConfirmDialogAction
  | CloseConfirmDialogAction;

type AccountState = {
  scanTaskStartTime: string | undefined;
  progress: number | undefined;
  stage: string | undefined;
  stages: string | undefined;
  percent: number | undefined;
  credential: AwsCredential | undefined;
  azureFiles: AzureLoadFiles | undefined;
  status: string;
  isCredentialDialogOpen: boolean;
  isConfirmDialogOpen: boolean;
  pendingScan: PendingScan | undefined;
  authType: AuthType | undefined;
  allCompliance: Record<string, RulesCompliance>;
  accountEntity: AccountEntity | undefined;
  cloudResourcesStatistics: AccountCloudResourcesStatistics | undefined;
  isSettingsOpen: boolean;
};

type AccountContextType = {
  scanTaskStartTime: string | undefined;
  progress: number | undefined;
  stage: string | undefined;
  stages: string | undefined;
  percent: number | undefined;
  isLoading: boolean;
  isProcessing: boolean;
  isCredentialDialogOpen: boolean;
  isConfirmDialogOpen: boolean;
  authType: AuthType | undefined;
  allCompliance: Record<string, RulesCompliance>;
  cloudResourcesStatistics: AccountCloudResourcesStatistics | undefined;
  isSettingsOpen: boolean;
  startAwsScan(credential?: AwsCredential): void;
  startAzureLoad(files: AzureLoadFiles): void;
  openCredentialDialog(): void;
  closeCredentialDialog(): void;
  openConfirmDialog(pendingScan?: PendingScan): void;
  closeConfirmDialog(): void;
  confirmStartScan(): void;
  setAuthType(authType: AuthType | undefined): void;
  setAccountEntity(accountEntity: AccountEntity | undefined): void;
  startScanButtonClick(): void;
  openSettings(): void;
  closeSettings(): void;
};

const initialRulesState: AccountState = {
  scanTaskStartTime: undefined,
  progress: undefined,
  stage: undefined,
  stages: undefined,
  percent: undefined,
  credential: undefined,
  azureFiles: undefined,
  isCredentialDialogOpen: false,
  isConfirmDialogOpen: false,
  pendingScan: undefined,
  status: 'initial_load',
  authType: undefined,
  allCompliance: {},
  accountEntity: undefined,
  cloudResourcesStatistics: undefined,
  isSettingsOpen: false,
};

export const AccountContext = createContext<AccountContextType | null>(null);

export function AccountContextProvider(
  props: Readonly<AccountContextProviderProps>,
) {
  const { accountName } = props;
  const asyncTasksApi = useApi<AsyncTasksApi>(asyncTasksApiRef);
  const errorApi = useApi<ErrorApi>(errorApiRef);
  const secretsStorageApi = useApi<SecretsStorageApi>(secretsStorageApiRef);
  const rulesStatisticsApi = useApi(rulesStatisticsApiRef);
  const cloudResourcesStatisticsApi = useApi<CloudResourcesStatisticsApi>(
    cloudResourcesStatisticsApiRef,
  );

  const rulesReducer = (
    state: AccountState,
    action: AccountAction,
  ): AccountState => {
    switch (action.type) {
      case ActionTypes.SCAN_STARTED: {
        const stateCopy = { ...state };
        stateCopy.scanTaskStartTime = action.payload.task?.createdAt;
        stateCopy.progress = action.payload.task?.progress;
        stateCopy.stage = action.payload.scanStatus?.stage;
        stateCopy.stages = action.payload.scanStatus?.stages;
        stateCopy.percent = action.payload.scanStatus?.percent;
        stateCopy.credential = undefined;
        stateCopy.status = 'wait';
        return stateCopy;
      }
      case ActionTypes.LOAD_COMPLETED: {
        const stateCopy = { ...state };
        if (action.payload.task) {
          stateCopy.scanTaskStartTime = action.payload.task.createdAt;
          stateCopy.progress = action.payload.task.progress;
        } else if (action.payload.taskFinished) {
          // Active-task lookup came back empty: the scan is over and the
          // provider must leave the processing state, otherwise isProcessing
          // sticks forever and blocks Scan/Settings.
          stateCopy.scanTaskStartTime = undefined;
          stateCopy.progress = undefined;
        }
        if (action.payload.scanStatus) {
          stateCopy.stage = action.payload.scanStatus.stage;
          stateCopy.stages = action.payload.scanStatus.stages;
          stateCopy.percent = action.payload.scanStatus.percent;
        }

        stateCopy.status = 'wait';
        return stateCopy;
      }
      case ActionTypes.INITIAL_LOAD_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.cloudResourcesStatistics =
          action.payload.cloudResourcesStatistics;
        if (action.payload.task) {
          stateCopy.scanTaskStartTime = action.payload.task.createdAt;
          stateCopy.progress = action.payload.task.progress;
        } else if (action.payload.taskFinished) {
          stateCopy.scanTaskStartTime = undefined;
          stateCopy.progress = undefined;
        }
        if (action.payload.scanStatus) {
          stateCopy.stage = action.payload.scanStatus.stage;
          stateCopy.stages = action.payload.scanStatus.stages;
          stateCopy.percent = action.payload.scanStatus.percent;
        }
        stateCopy.status = 'wait';
        return stateCopy;
      }
      case ActionTypes.START_SCAN: {
        const stateCopy = { ...state };
        stateCopy.credential = action.payload.credential;
        stateCopy.azureFiles = action.payload.azureFiles;
        stateCopy.status = 'start_scan';
        return stateCopy;
      }
      case ActionTypes.CHANGE_CREDENTIAL_DIALOG_STATUS: {
        const stateCopy = { ...state };
        stateCopy.isCredentialDialogOpen = action.payload;
        return stateCopy;
      }
      case ActionTypes.OPEN_CONFIRM_DIALOG: {
        const stateCopy = { ...state };
        stateCopy.pendingScan = action.payload;
        stateCopy.isConfirmDialogOpen = true;
        return stateCopy;
      }
      case ActionTypes.CLOSE_CONFIRM_DIALOG: {
        const stateCopy = { ...state };
        stateCopy.pendingScan = undefined;
        stateCopy.isConfirmDialogOpen = false;
        return stateCopy;
      }
      case ActionTypes.CHANGE_AUTH_TYPE: {
        const stateCopy = { ...state };
        stateCopy.authType = action.payload;
        return stateCopy;
      }
      case ActionTypes.COMPLIANCE_LOADED: {
        const stateCopy = { ...state };
        stateCopy.allCompliance = action.payload;
        return stateCopy;
      }
      case ActionTypes.OPEN_SETTINGS: {
        const stateCopy = { ...state };
        stateCopy.isSettingsOpen = true;
        return stateCopy;
      }
      case ActionTypes.CLOSE_SETTINGS: {
        const stateCopy = { ...state };
        stateCopy.isSettingsOpen = false;
        return stateCopy;
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(rulesReducer, initialRulesState);

  const scanTaskType =
    state.authType === AuthType.LOAD_AZURE
      ? AsyncTaskType.LoadAzure
      : AsyncTaskType.ScanAws;

  useEffect(() => {
    const fetchScanTask = async () => {
      if (state.status === 'initial_load') return;

      try {
        const activeTasks = await asyncTasksApi.getActiveTasks(
          state.accountEntity?.metadata.name ?? accountName,
          scanTaskType,
        );

        let task = undefined;

        if (activeTasks.tasks.length > 0) {
          task = activeTasks.tasks[0];
        }

        dispatch({
          type: ActionTypes.LOAD_COMPLETED,
          payload: {
            task: task,
            scanStatus: activeTasks.scanStatus,
            taskFinished: task === undefined,
          },
        });
      } catch (error) {
        const err = error as Error;
        errorApi.post(err);
        dispatch({
          type: ActionTypes.LOAD_COMPLETED,
          payload: { task: undefined, scanStatus: undefined },
        });
      }
    };

    fetchScanTask();
    const intervalId = setInterval(fetchScanTask, 60000);

    return () => clearInterval(intervalId);
  }, [
    asyncTasksApi,
    errorApi,
    accountName,
    state.accountEntity?.metadata.name,
    state.status,
    scanTaskType,
  ]);

  useEffect(() => {
    const initialLoad = async () => {
      try {
        const response =
          await cloudResourcesStatisticsApi.getAccountCloudResourcesStatistics(
            accountName,
          );

        const activeTasks = await asyncTasksApi.getActiveTasks(
          state.accountEntity?.metadata.name ?? accountName,
          scanTaskType,
        );

        let task = undefined;

        if (activeTasks.tasks.length > 0) {
          task = activeTasks.tasks[0];
        }

        dispatch({
          type: ActionTypes.INITIAL_LOAD_COMPLETED,
          payload: {
            task: task,
            scanStatus: activeTasks.scanStatus,
            cloudResourcesStatistics: response.body
              ?.item as AccountCloudResourcesStatistics,
            taskFinished: task === undefined,
          },
        });
      } catch (error) {
        const err = error as Error;
        errorApi.post(err);
        dispatch({
          type: ActionTypes.INITIAL_LOAD_COMPLETED,
          payload: {
            task: undefined,
            scanStatus: undefined,
            cloudResourcesStatistics: undefined,
          },
        });
      }
    };

    if (state.status !== 'initial_load') {
      return;
    }

    initialLoad();
  }, [
    accountName,
    cloudResourcesStatisticsApi,
    errorApi,
    asyncTasksApi,
    state.accountEntity?.metadata.name,
    state.status,
    scanTaskType,
  ]);

  useEffect(() => {
    const startScanTask = async () => {
      try {
        if (state.authType === AuthType.LOAD_AZURE) {
          if (state.azureFiles === undefined) {
            throw new Error('Azure files are undefined');
          }

          const taskId = await asyncTasksApi.startAsyncTask(
            AsyncTaskType.LoadAzure,
            {
              accountId: accountName,
              provider: 'azure',
              inventoryFileName: state.azureFiles.inventory.fileName,
              inventoryContent: state.azureFiles.inventory.content,
              reportFileName: state.azureFiles.report?.fileName,
              reportContent: state.azureFiles.report?.content,
            },
            accountName,
          );

          const taskStatusResponse = await asyncTasksApi.getTaskInfo(taskId);

          dispatch({
            type: ActionTypes.SCAN_STARTED,
            payload: {
              task: taskStatusResponse.task,
              scanStatus: taskStatusResponse.scanStatus,
            },
          });
          return;
        }

        if (state.authType === AuthType.AWS_ACCESS_KEY) {
          if (state.credential === undefined) {
            throw new Error('Credential is undefined');
          }

          // The scan task reads these secrets on the backend: persist them
          // (and surface a failure) before the task is started.
          await Promise.all([
            secretsStorageApi.setTemporarySecret(
              `${accountName}-access-key`,
              state.credential.accessKey,
            ),
            secretsStorageApi.setTemporarySecret(
              `${accountName}-secret-access-key`,
              state.credential.secretAccessKey,
            ),
            secretsStorageApi.setTemporarySecret(
              `${accountName}-secret-token`,
              state.credential.secretToken ?? '',
            ),
          ]);
        }

        const taskId = await asyncTasksApi.startAsyncTask(
          AsyncTaskType.ScanAws,
          {
            accountId: accountName,
            isLocal: state.credential === undefined,
          },
          accountName,
        );

        const taskStatusResponse = await asyncTasksApi.getTaskInfo(taskId);

        dispatch({
          type: ActionTypes.SCAN_STARTED,
          payload: {
            task: taskStatusResponse.task,
            scanStatus: taskStatusResponse.scanStatus,
          },
        });
      } catch (error) {
        const err = error as Error;
        errorApi.post(err);
        dispatch({
          type: ActionTypes.SCAN_STARTED,
          payload: { task: undefined, scanStatus: undefined },
        });
      }
    };

    if (state.status !== 'start_scan') {
      return;
    }

    startScanTask();
  }, [
    asyncTasksApi,
    errorApi,
    secretsStorageApi,
    state.authType,
    state.credential,
    state.azureFiles,
    state.status,
    accountName,
  ]);

  useEffect(() => {
    let ignore = false;

    const fetchCompliance = async () => {
      try {
        const allCompliance =
          await rulesStatisticsApi.getCompliance(accountName);
        if (ignore) return;
        dispatch({
          type: ActionTypes.COMPLIANCE_LOADED,
          payload: allCompliance,
        });
      } catch (error) {
        if (ignore) return;
        errorApi.post(error as Error);
        dispatch({
          type: ActionTypes.COMPLIANCE_LOADED,
          payload: {},
        });
      }
    };

    fetchCompliance();

    return () => {
      ignore = true;
    };
  }, [rulesStatisticsApi, errorApi, accountName]);

  const context = useMemo(() => {
    const startAwsScan = (credential?: AwsCredential) =>
      dispatch({
        type: ActionTypes.START_SCAN,
        payload: { credential: credential, azureFiles: undefined },
      });
    const startAzureLoad = (files: AzureLoadFiles) =>
      dispatch({
        type: ActionTypes.START_SCAN,
        payload: { credential: undefined, azureFiles: files },
      });
    const openCredentialDialog = () =>
      dispatch({
        type: ActionTypes.CHANGE_CREDENTIAL_DIALOG_STATUS,
        payload: true,
      });
    const closeCredentialDialog = () =>
      dispatch({
        type: ActionTypes.CHANGE_CREDENTIAL_DIALOG_STATUS,
        payload: false,
      });
    const openConfirmDialog = (pendingScan?: PendingScan) =>
      dispatch({ type: ActionTypes.OPEN_CONFIRM_DIALOG, payload: pendingScan });
    const closeConfirmDialog = () =>
      dispatch({ type: ActionTypes.CLOSE_CONFIRM_DIALOG });
    const confirmStartScan = () => {
      const pendingScan = state.pendingScan;
      dispatch({ type: ActionTypes.CLOSE_CONFIRM_DIALOG });
      dispatch({
        type: ActionTypes.START_SCAN,
        payload: {
          credential: pendingScan?.credential,
          azureFiles: pendingScan?.azureFiles,
        },
      });
    };
    const setAuthType = (authType: AuthType | undefined) =>
      dispatch({ type: ActionTypes.CHANGE_AUTH_TYPE, payload: authType });
    const setAccountEntity = (accountEntity: AccountEntity | undefined) =>
      dispatch({
        type: ActionTypes.CHANGE_ACCOUNT_ENTITY,
        payload: accountEntity,
      });
    const openSettings = () => dispatch({ type: ActionTypes.OPEN_SETTINGS });
    const closeSettings = () => dispatch({ type: ActionTypes.CLOSE_SETTINGS });

    const startScanButtonClick = () => {
      if (state.authType === AuthType.AWS_INSTANCE_PROFILE) {
        // Instance-profile scans need no input; ask for confirmation before
        // starting the scan right away.
        openConfirmDialog();
        return;
      }
      // AWS access key accounts ask for credentials, Azure accounts pick a
      // scan result file to load; the rendered dialog depends on authType.
      // Both then continue to the confirmation dialog.
      openCredentialDialog();
    };

    return {
      scanTaskStartTime: state.scanTaskStartTime,
      progress: state.progress,
      stage: state.stage,
      stages: state.stages,
      percent: state.percent,
      isLoading: state.status === 'initial_load',
      isProcessing:
        (state.status !== 'wait' && state.status !== 'initial_load') ||
        state.scanTaskStartTime !== undefined,
      isCredentialDialogOpen: state.isCredentialDialogOpen,
      isConfirmDialogOpen: state.isConfirmDialogOpen,
      authType: state.authType,
      allCompliance: state.allCompliance,
      cloudResourcesStatistics: state.cloudResourcesStatistics,
      isSettingsOpen: state.isSettingsOpen,
      startAwsScan: startAwsScan,
      startAzureLoad: startAzureLoad,
      openCredentialDialog: openCredentialDialog,
      closeCredentialDialog: closeCredentialDialog,
      openConfirmDialog: openConfirmDialog,
      closeConfirmDialog: closeConfirmDialog,
      confirmStartScan: confirmStartScan,
      setAuthType: setAuthType,
      setAccountEntity: setAccountEntity,
      startScanButtonClick: startScanButtonClick,
      openSettings: openSettings,
      closeSettings: closeSettings,
    };
  }, [
    state.scanTaskStartTime,
    state.progress,
    state.stage,
    state.stages,
    state.percent,
    state.status,
    state.isCredentialDialogOpen,
    state.isConfirmDialogOpen,
    state.pendingScan,
    state.authType,
    state.allCompliance,
    state.cloudResourcesStatistics,
    state.isSettingsOpen,
  ]);

  return (
    <AccountContext.Provider value={context}>
      {props.children}
    </AccountContext.Provider>
  );
}
